import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Award, Target, Heart, TrendingUp } from 'lucide-react';
import { siteConfig } from '@/config/siteConfig';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${(BACKEND_URL || '').replace(/\/$/, '')}/api`;

export default function About() {
  const navigate = useNavigate();
  const [isAgentPhotoLoading, setIsAgentPhotoLoading] = useState(true);
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [agentPhotoUrl, setAgentPhotoUrl] = useState('');

  useEffect(() => {
    const fetchAgentPhoto = async () => {
      console.log('About.jsx: [1] Starting to fetch agent photo...');
      setIsAgentPhotoLoading(true);
      try {
        const response = await axios.get(`${API}/agent-photo`);
        console.log('About.jsx: [2] API response received:', response.data);
        if (response.data.photo_url) {
          console.log('About.jsx: [3a] Photo URL found, setting state:', response.data.photo_url);
          setAgentPhotoUrl(response.data.photo_url);
        } else {
          console.log('About.jsx: [3b] No photo URL in response, setting fallback.');
          setAgentPhotoUrl("/images/agent-photo.jpg"); // Fallback
        }
      } catch (error) {
        console.error('Failed to fetch agent photo, using fallback:', error);
        console.log('About.jsx: [3c] Error fetching photo, setting fallback.');
        setAgentPhotoUrl("/images/agent-photo.jpg"); // Fallback on error
      }
    };
    fetchAgentPhoto();
  }, []);

  useEffect(() => {
    if (!agentPhotoUrl) {
      console.log('About.jsx: [4] Preload effect skipped: agentPhotoUrl is empty.');
      return;
    }

    console.log('About.jsx: [5] Starting image preload for:', agentPhotoUrl);
    const img = new Image();
    img.src = agentPhotoUrl;
    img.onload = () => {
      console.log('About.jsx: [6a] Image successfully preloaded.');
      setIsImageLoaded(true);
    };
    img.onerror = () => {
      console.error('About.jsx: [6b] Failed to preload image.');
      setIsAgentPhotoLoading(false); // Stop loading skeleton if image fails
    };
  }, [agentPhotoUrl]);

  const values = [
    {
      icon: <Heart className="h-8 w-8 text-[#d4af37]" />,
      title: 'Client-Focused',
      description: 'Your needs and dreams are my top priority. I listen, understand, and deliver personalized solutions.'
    },
    {
      icon: <Target className="h-8 w-8 text-[#d4af37]" />,
      title: 'Market Expertise',
      description: 'Deep knowledge of California and Florida markets ensures you get the best deals and opportunities.'
    },
    {
      icon: <TrendingUp className="h-8 w-8 text-[#d4af37]" />,
      title: 'Proven Track Record',
      description: 'Over 10 years of successful transactions and satisfied clients across two states.'
    },
    {
      icon: <Award className="h-8 w-8 text-[#d4af37]" />,
      title: 'Licensed Professional',
      description: 'Fully licensed and certified in both California and Florida with continuous professional development.'
    }
  ];

  const areas = [
    {
      state: 'California',
      regions: siteConfig.serviceAreas.california,
      image: 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800&q=80'
    },
    {
      state: 'Florida',
      regions: siteConfig.serviceAreas.florida,
      image: 'https://images.unsplash.com/photo-1527004013197-933c4bb611b3?w=800&q=80'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 to-blue-50 py-20" data-testid="about-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-4xl sm:text-5xl font-bold text-[#1e3a8a] mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
                Meet Raj Manda
              </h1>
              <p className="text-lg text-slate-600 mb-4">
                I'm a dedicated real estate professional with a passion for helping people find their perfect home. With licenses in both California and Florida, I bring extensive market knowledge and a commitment to exceptional service.
              </p>
              <p className="text-lg text-slate-600 mb-6">
                Whether you're a first-time buyer, looking to upgrade, or investing in property, I'll guide you through every step with expertise and care.
              </p>
              <div className="space-y-2 text-slate-700 mb-8">
                <p><strong>California DRE#:</strong> {siteConfig.californiaLicense.replace('CA DRE#: ', '')}</p>
                <p><strong>Florida License#:</strong> {siteConfig.floridaLicense.replace('FL License#: ', '')}</p>
                <p><strong>Years of Experience:</strong> {siteConfig.yearsOfExperience}</p>
              </div>
              <Button
                data-testid="about-contact-btn"
                onClick={() => navigate('/contact')}
                className="bg-[#1e3a8a] hover:bg-[#1e3a8a]/90 text-white"
              >
                Get in Touch
              </Button>
            </div>
            <div className="relative">
              <div className="relative w-full h-[600px]">
                {/* Loading Skeleton */}
                {!isImageLoaded && isAgentPhotoLoading && (
                  <div className="absolute inset-0 bg-gray-100 animate-pulse rounded-lg shadow-2xl" />
                )}
                
                {/* Agent's Photo */}
                {agentPhotoUrl && (
                  <img
                    src={agentPhotoUrl}
                    alt="Raj Manda - Licensed Realtor"
                    className={`rounded-lg shadow-2xl w-full h-full object-cover transition-opacity duration-300 ${
                      isImageLoaded ? 'opacity-100' : 'opacity-0'
                    }`}
                    style={{ objectPosition: 'center 20%' }}
                    data-testid="about-hero-image"
                    loading="eager"
                    onLoad={() => {
                      console.log('About.jsx: [7a] <img> tag onLoad event fired.');
                      setIsImageLoaded(true);
                      setIsAgentPhotoLoading(false);
                    }}
                    onError={() => {
                      console.error('About.jsx: [7b] <img> tag onError event fired.');
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-white" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Why Choose Me
            </h2>
            <p className="text-lg text-slate-600">
              My commitment to excellence sets me apart
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="hover:shadow-xl transition-shadow" data-testid="value-card">
                <CardContent className="pt-6 text-center">
                  <div className="flex justify-center mb-4">
                    {value.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-[#1e3a8a] mb-3">
                    {value.title}
                  </h3>
                  <p className="text-slate-600">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-20 bg-slate-50" data-testid="areas-served-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Areas I Serve
            </h2>
            <p className="text-lg text-slate-600">
              Bringing local expertise to California and Florida markets
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {areas.map((area, index) => (
              <Card key={index} className="overflow-hidden hover:shadow-xl transition-shadow" data-testid="area-card">
                <div className="h-64 overflow-hidden">
                  <img
                    src={area.image}
                    alt={area.state}
                    className="w-full h-full object-cover hover:scale-110 transition-transform duration-300"
                  />
                </div>
                <CardContent className="pt-6">
                  <h3 className="text-2xl font-bold text-[#1e3a8a] mb-4">
                    {area.state}
                  </h3>
                  <ul className="space-y-2">
                    {area.regions.map((region, idx) => (
                      <li key={idx} className="text-slate-600 flex items-center">
                        <span className="w-2 h-2 bg-[#d4af37] rounded-full mr-3"></span>
                        {region}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Video Section Placeholder */}
      <section className="py-20 bg-white" data-testid="video-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#1e3a8a] mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Success Stories
            </h2>
            <p className="text-lg text-slate-600">
              Watch how I've helped clients achieve their real estate dreams
            </p>
          </div>

          <div className="aspect-video bg-slate-200 rounded-lg flex items-center justify-center" data-testid="video-placeholder">
            <div className="text-center">
              <p className="text-slate-500 mb-2">Video Placeholder</p>
              <p className="text-sm text-slate-400">Upload your success story video here</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#1e3a8a] text-white" data-testid="about-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
            Let's Work Together
          </h2>
          <p className="text-xl mb-8">
            Ready to start your home buying journey? I'm here to help every step of the way.
          </p>
          <Button
            data-testid="about-cta-signup-btn"
            onClick={() => navigate('/signup')}
            size="lg"
            className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-[#1e3a8a] text-lg px-8 py-6 font-semibold"
          >
            Get Started Today
          </Button>
        </div>
      </section>
    </div>
  );
}
